import AutorenewIcon from '@mui/icons-material/Autorenew'
import DomainAddIcon from '@mui/icons-material/DomainAdd'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import { Box, Tooltip, Typography } from '@mui/material'
import { GridRenderCellParams } from '@mui/x-data-grid-premium'
import React from 'react'
import { DensityStyleTypes } from 'types'

// Change column — populated by the backend's productsClassification module on
// /search/products. Compares this product against the matching product on the
// prior deal version (by productName); New Business is org-wide (no other
// deal of this org has ever had this product_type).
//
// The cell's underlying VALUE is the dollar delta vs prior version so the
// column sorts / filters / aggregates by revenue impact:
//   New Business → +full new price (new revenue)
//   Upsell       → +(current − prior)
//   Downsell     → −(prior − current)
//   Renewal      → 0
// The cell DISPLAY is the change-type icon as a left adornment (flush to the
// column edge) plus the formatted delta on the right in green / red / default.

type ChangeKey = 'New Business' | 'Upsell' | 'Downsell' | 'Renewal' | 'Removed'

const ICON_BY_CHANGE: Record<
  ChangeKey,
  { Icon: typeof DomainAddIcon; color: string }
> = {
  'New Business': { Icon: DomainAddIcon, color: 'primary.main' },
  Upsell: { Icon: TrendingUpIcon, color: 'success.main' },
  Downsell: { Icon: TrendingDownIcon, color: 'warning.main' },
  Renewal: { Icon: AutorenewIcon, color: 'text.secondary' },
  Removed: { Icon: RemoveCircleOutlineIcon, color: 'error.main' },
}

const formatUSD = (n: number): string =>
  n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

const formatSignedUSD = (n: number): string => {
  if (n === 0) return '$0'
  return (n > 0 ? '+' : '') + formatUSD(n)
}

interface ProductChangeCellProps {
  params: GridRenderCellParams
  densityStyles: DensityStyleTypes
  /** When the toolbar change-type filter is set, the agg row uses it to pick the right icon. */
  activeChangeType?: ChangeKey
}

const ProductChangeCell: React.FC<ProductChangeCellProps> = ({ params, densityStyles, activeChangeType }) => {
  // ─── Aggregation footer row ────────────────────────────────────────────
  // The row carries the pre-summed delta in `change_type` (as a number) and
  // gets `isAggregationRow: true`. We adopt the active filter's icon so the
  // total visually matches whatever the user is looking at; with no filter
  // there's no single owning change type, so we omit the icon.
  if (params?.row?.isAggregationRow) {
    const total = Number(params.value ?? 0)
    const color = total > 0 ? 'success.main' : total < 0 ? 'error.main' : 'text.secondary'
    const display = formatSignedUSD(total)
    const aggIcon = activeChangeType ? ICON_BY_CHANGE[activeChangeType] : null

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '100%',
          width: '100%',
          // minWidth:0 lets the Typography flex child actually shrink — without
          // it the default min-width:auto pins to content width and prevents
          // ellipsis from ever kicking in.
          minWidth: 0,
          overflow: 'hidden',
          gap: densityStyles.gap,
        }}
      >
        {aggIcon ? (
          <aggIcon.Icon
            sx={{
              color: aggIcon.color,
              flexShrink: 0,
              fontSize: densityStyles?.iconSize,
            }}
          />
        ) : (
          <span style={{ flexShrink: 0 }} />
        )}
        <Typography
          variant="body2"
          sx={{
            color,
            fontWeight: 700,
            fontSize: densityStyles?.fontSize,
            textAlign: 'right',
            // Truncate when the column is narrower than the formatted total
            // (matches the other aggregate cells via AggregateCurrencyCell).
            minWidth: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {display}
        </Typography>
      </Box>
    )
  }

  // ─── Data row ──────────────────────────────────────────────────────────
  const change = params?.row?.change_type as ChangeKey | null | undefined
  if (!change) return null

  const { Icon, color: iconColor } = ICON_BY_CHANGE[change]

  const delta = Number(params.value ?? 0)
  const priorGp = params?.row?.prior_gross_profit as number | null | undefined
  const currentGp = Number(params?.row?.grossProfit ?? 0)
  const valueColor = delta > 0 ? 'success.main' : delta < 0 ? 'error.main' : 'text.secondary'
  const display = formatSignedUSD(delta)

  let tooltip = `${change}`
  if (change === 'New Business') {
    tooltip = `New Business — first time this org has a product of this service line (monthly GP ${formatUSD(currentGp)})`
  } else if (change === 'Renewal' && priorGp != null) {
    tooltip = `Renewal — same monthly gross profit as prior version (${formatUSD(priorGp)})`
  } else if ((change === 'Upsell' || change === 'Downsell') && priorGp != null) {
    tooltip = `${change} — prior monthly gross profit was ${formatUSD(priorGp)} → now ${formatUSD(currentGp)}`
  } else if (change === 'Removed') {
    tooltip = `Removed — this product was dropped from the next deal version (lost ${formatUSD(currentGp)} monthly GP)`
  }

  return (
    <Tooltip title={tooltip} arrow>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          // Icon flush to the column's left edge, value flush to the right —
          // makes every row's icon align in the same column regardless of
          // delta digit width.
                  textOverflow: 'ellipsis',
        overflow: 'hidden',
          justifyContent: 'space-between',
          height: '100%',
          width: '100%',
          gap: densityStyles.gap,
        }}
      >
        <Icon
          sx={{
            color: iconColor,
            flexShrink: 0,
            fontSize: densityStyles?.iconSize,
          }}
        />
        <Typography
          variant="body2"
          sx={{
            color: valueColor,
            fontWeight: 600,
                      textOverflow: 'ellipsis',
          overflow: 'hidden',
            fontSize: densityStyles?.fontSize,
            textAlign: 'right',
          }}
        >
          {display}
        </Typography>
      </Box>
    </Tooltip>
  )
}

export default ProductChangeCell
